import React, {memo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {intlFormatDistance} from 'date-fns/intlFormatDistance'
import {getLanguageCode} from 'modules/meta/selectors'

import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {NoStyleButton} from '@vio/atlas/src/components/designSystem/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/src/themes'
import {TrustPilotReview} from '@vio/core/src/trustpilotWidget/types'
import {TrustpilotSvg} from '@vio/shared/src/search/TrustpilotWidget'

import {TRUSTPILOT_LABELS} from '../constants'

const ReviewText = styled(Text)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  line-clamp: 3;
  -webkit-line-clamp: 3;
`

const ReviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ReviewRate = styled.img`
  width: 108px;
`

const Separator = styled.span`
  padding: 0 ${cssTheme.layout.spacing.s200};
`

const ReviewCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${cssTheme.layout.spacing.s400};
  border: 1px solid;
  border-color: ${cssTheme.colors.border.success.secondary};
  background-color: ${cssTheme.colors.background.success.tertiary};
  border-radius: ${cssTheme.layout.radius.md};
  .--trustpilot-logo {
    width: auto;
    height: 18px;
  }
  ${ReviewRate} {
    padding-bottom: ${cssTheme.layout.spacing.s300};
  }
`

const ReviewByline = ({review}: {review: TrustPilotReview}) => {
  const languageCode = useSelector(getLanguageCode)

  return (
    <Box display="flex" alignItems="center">
      <Text colorPath="content.neutral.secondary" variant="labelXS">
        {review.consumer.displayName}
      </Text>
      <Separator>·</Separator>
      <Text colorPath="content.neutral.secondary" variant="bodyXS">
        {intlFormatDistance(new Date(review.createdAt), new Date(), {
          locale: languageCode,
          numeric: 'always'
        })}
      </Text>
    </Box>
  )
}

interface Props {
  review: TrustPilotReview
  isReviewCard?: boolean
  hasDivider?: boolean
  onHideReviewCard?: () => void
  onClick?: () => void
}

const TrustpilotReviewItemComponent = ({
  review,
  isReviewCard,
  hasDivider,
  onHideReviewCard,
  onClick
}: Props) => {
  const {formatMessage} = useIntl()

  if (!review || !review?.id) return null

  if (isReviewCard)
    return (
      <ReviewCardWrapper>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="s400"
        >
          <TrustpilotSvg />
          {onHideReviewCard && (
            <NoStyleButton onClick={onHideReviewCard}>
              <Icon
                name="Close"
                size="md"
                colorPath="content.neutral.secondary"
              />
            </NoStyleButton>
          )}
        </Box>
        <ReviewRate
          src={`https://images-static.trustpilot.com/api/stars/${review.stars}/star.svg`}
          alt="review-rate"
        />
        <ReviewText variant="labelS">{review.text}</ReviewText>
        <Box
          display="flex"
          alignItems="center"
          paddingTop="s200"
          paddingBottom="s400"
        >
          <ReviewByline review={review} />
        </Box>
        <Button onClick={onClick} fullWidth variant="tertiary" size="md">
          {formatMessage(TRUSTPILOT_LABELS.seeOtherReviews)}
        </Button>
      </ReviewCardWrapper>
    )

  return (
    <ReviewWrapper>
      <ReviewRate
        src={`https://images-static.trustpilot.com/api/stars/${review.stars}/star.svg`}
        alt="review-rate"
      />
      <Box paddingTop="s300">
        <ReviewText variant="labelS">{review.text}</ReviewText>
      </Box>
      <Box display="flex" alignItems="center" gap="s250" paddingTop="s200">
        <ReviewByline review={review} />
      </Box>
      {hasDivider && (
        <Box marginBottom="s500" marginTop="s500">
          <Divider />
        </Box>
      )}
    </ReviewWrapper>
  )
}

export const TrustpilotReviewItem = memo(TrustpilotReviewItemComponent)
